import { useState } from "react";
import { Button, Form, Modal, Select, Table, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { ListPage } from "../../components/ListPage";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { hasAnyGrant } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { useApi } from "../../helpers/useApi";
import { useSuppliersQuery } from "../../helpers/queries";
import type { SupplierReturn, SiteReturn } from "../../api-models";

// Stock only ever reaches a supplier return through a SUPPLIER_RETURN disposition on a
// completed site return (spec §20 — site→supplier is never direct), so creating one is a
// matter of picking which open dispositions to claim rather than typing quantities.
interface ClaimableDisposition {
  id: number;
  qty: number;
  itemCode: string;
  itemName: string;
  siteReturnDocNo: string;
}

export default function SupplierReturnsListPage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const { request, loading } = useApi();
  const { data: suppliers = [] } = useSuppliersQuery();
  const canWrite = hasAnyGrant(user, "SupplierReturns.Write");

  const [open, setOpen] = useState(false);
  const [claimable, setClaimable] = useState<ClaimableDisposition[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [form] = Form.useForm();

  // The claimable set is derived client-side from completed site returns: their
  // SUPPLIER_RETURN dispositions that no supplier return line has claimed yet.
  async function openDialog() {
    const page = await request<{ data: SiteReturn[] }>({ url: "/site-returns", params: { pageSize: 200 } });
    const rows: ClaimableDisposition[] = [];
    for (const sr of page.data ?? []) {
      const detail = await request<SiteReturn>({ url: `/site-returns/${sr.id}` });
      for (const line of detail.lines ?? []) {
        for (const d of line.dispositions ?? []) {
          if (d.type === "SUPPLIER_RETURN" && !d.supplierReturnLineId) {
            rows.push({
              id: d.id,
              qty: d.qty,
              itemCode: line.item?.code ?? String(line.itemId),
              itemName: line.item?.name ?? "",
              siteReturnDocNo: detail.docNo,
            });
          }
        }
      }
    }
    setClaimable(rows);
    setSelected([]);
    form.resetFields();
    setOpen(true);
  }

  async function create() {
    const values = await form.validateFields();
    if (selected.length === 0) {
      message.warning("Select at least one held quantity to return.");
      return;
    }
    const created = await request<SupplierReturn>({
      method: "POST",
      url: "/supplier-returns",
      data: { ...values, items: selected.map((id) => ({ sourceDispositionId: id })) },
    });
    message.success("Supplier return created.");
    setOpen(false);
    navigate(`/supplier-returns/${created.id}`);
  }

  return (
    <>
      <ListPage<SupplierReturn>
        title="Supplier Returns"
        endpoint="/supplier-returns"
        searchPlaceholder="Search doc no or reason..."
        linkTo={(r) => `/supplier-returns/${r.id}`}
        actions={
          canWrite && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openDialog} loading={loading}>
              New supplier return
            </Button>
          )
        }
        columns={[
          { title: "Doc No", dataIndex: "docNo", sorter: true },
          { title: "Supplier", render: (_, r) => r.supplier?.name },
          { title: "Reason", dataIndex: "reason", sorter: true, ellipsis: true },
          { title: "Status", dataIndex: "status", sorter: true, render: (v) => <DocumentStatusTag status={v} /> },
        ]}
        renderCard={(r) => (
          <div>
            <strong>{r.docNo}</strong>
            <div>{r.supplier?.name}</div>
            <DocumentStatusTag status={r.status} />
          </div>
        )}
      />

      <Modal title="New supplier return" open={open} onCancel={() => setOpen(false)} onOk={create} confirmLoading={loading} width={720} destroyOnHidden>
        <Form form={form} layout="vertical">
          <Form.Item name="supplierId" label="Supplier" rules={[{ required: true }]}>
            <Select options={suppliers.map((s) => ({ value: s.id, label: s.name }))} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Select
              mode="tags"
              maxCount={1}
              placeholder="Why is this going back to the supplier?"
              options={["Defective batch", "Wrong specification", "Short-dated", "Over-supplied"].map((v) => ({ value: v }))}
            />
          </Form.Item>
        </Form>
        <Table
          size="small"
          rowKey="id"
          dataSource={claimable}
          pagination={false}
          locale={{ emptyText: "Nothing is being held at Return Holding for a supplier return yet." }}
          rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys as number[]) }}
          columns={[
            { title: "Code", dataIndex: "itemCode" },
            { title: "Item", dataIndex: "itemName" },
            { title: "Qty", dataIndex: "qty" },
            { title: "From site return", dataIndex: "siteReturnDocNo" },
          ]}
        />
      </Modal>
    </>
  );
}
